import { useEffect, useMemo, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import {
  apiService, ContainerApplyEntry, ContainerApplyResult, PackCosmetic, PremiumData,
} from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import {
  ApplyReport, ContainerScope, ScopeEntry, applyResultOf, useContainersData, useProjectContainers,
} from '@/components/config/AllContainers';
import styles from '@/pages/GradesPage.module.scss';

const NOT_FOUND: PremiumData = { found: false, file: null, groups: [], nametags: [], ranktags: [] };

interface Look { nameTag: string; rankTag: string }

/** Brouillon d'un grade : l'apparence, et `retry` quand un container n'a pas reçu le dernier enregistrement. */
interface LookDraft extends Look { retry?: boolean }

const LOOK_LABELS: Record<keyof Look, string> = { nameTag: 'ornement', rankTag: 'rank tag' };

/** Ornement et rank tag d'un grade dans la config premium d'un container. */
function lookOf(data: PremiumData | undefined, grade: string): Look {
  const g = data?.groups.find((x) => x.name === grade);
  return {
    nameTag: (g?.values?.NameTagCosmetic as string) ?? '',
    rankTag: (g?.values?.RankTagCosmetic as string) ?? '',
  };
}

function differingLook(a: Look, b: Look): string[] {
  return (Object.keys(LOOK_LABELS) as Array<keyof Look>).filter((k) => a[k] !== b[k]);
}

/** Aperçu d'une texture cosmetic (nametag/ranktag), résolue via le render controller du pack.
 * `index` (position dans le pack) permet l'aperçu même si le cosmetic n'est pas encore déclaré au serveur. */
function CosmeticPreview({ containerId, type, name, index }: { containerId: string; type: 'nametag' | 'ranktag'; name: string; index?: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!name || !containerId) { setUrl(null); return; }
    let active = true;
    let created: string | null = null;
    setLoading(true);
    apiService.getCosmeticBlobUrl(containerId, type, name, undefined, index).then((r) => {
      if (!active) { if (r) URL.revokeObjectURL(r); return; }
      created = r; setUrl(r); setLoading(false);
    });
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [containerId, type, name, index]);
  return (
    <div className={styles.cosmeticPreview}>
      {loading ? <Loader2 size={16} className={styles.spin} /> : url ? <img src={url} alt={name} /> : <span className={styles.cosmeticMissing}>{name ? 'texture introuvable' : '—'}</span>}
    </div>
  );
}

/** Une valeur enregistrée qui n'existe pas dans le pack : plus aucune texture derrière. */
function staleOf(value: string, pack: PackCosmetic[]): boolean {
  return !!value && !pack.some((p) => p.name === value);
}

/**
 * Sélecteur de cosmetic alimenté par le PACK (ce qui existe réellement), et non par le registre du
 * serveur : celui-ci peut encore lister les cosmetics d'un autre projet ou d'une version antérieure.
 * Les entrées pas encore déclarées au serveur sont marquées (elles le seront à l'enregistrement) et les
 * valeurs absentes du pack sont isolées dans un groupe à part pour ne pas les perdre silencieusement.
 */
function CosmeticSelect({
  value, onChange, disabled, pack, declared,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  pack: PackCosmetic[];
  declared: string[];
}) {
  const stale = declared.filter((n) => !pack.some((p) => p.name === n));
  if (value && !pack.some((p) => p.name === value) && !stale.includes(value)) stale.push(value);
  return (
    <select className={styles.input} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">(aucun)</option>
      {pack.length > 0 && (
        <optgroup label="Dans le pack">
          {pack.map((p) => (
            <option key={p.name} value={p.name}>
              {declared.includes(p.name) ? p.name : `${p.name} (à déclarer)`}
            </option>
          ))}
        </optgroup>
      )}
      {stale.length > 0 && (
        <optgroup label="Absents du pack (aucune texture)">
          {stale.map((n) => <option key={n} value={n}>{n}</option>)}
        </optgroup>
      )}
    </select>
  );
}

/**
 * Apparence d'un grade (Supervision > Grades, onglet Cosmétiques) : nametag (ornement) + ranktag,
 * choisis dans le catalogue du pack, avec aperçu. Commun à tous les containers du projet : lu dans le premier container (par id) qui porte la config premium,
 * enregistré (barre du bas) dans chaque container qui la porte, chacun déclarant au passage les
 * cosmetics choisis à l'index du pack.
 */
export function GradeApparence({ grade, readOnly = false }: { grade: string; readOnly?: boolean }) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const { containers, loaded } = useProjectContainers();
  const [reload, setReload] = useState(0);
  const { data: all } = useContainersData(
    containers,
    (id) => apiService.getContainerPremium(id).then((r) => r.data ?? NOT_FOUND),
    reload,
  );
  const [nameTag, setNameTag] = useState('');
  const [rankTag, setRankTag] = useState('');
  const [orig, setOrig] = useState<Look>({ nameTag: '', rankTag: '' });
  /**
   * Dernier enregistrement en échec sur au moins un container. La référence, elle, a pu être écrite :
   * relue, elle égale le formulaire, et sans ce drapeau le brouillon quitterait la barre alors qu'un
   * container porte encore les anciennes valeurs.
   */
  const [retry, setRetry] = useState(false);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);
  const [packNametags, setPackNametags] = useState<PackCosmetic[]>([]);
  const [packRanktags, setPackRanktags] = useState<PackCosmetic[]>([]);

  // Ce qui existe VRAIMENT dans le pack du projet : c'est ça qu'on propose, pas le registre du serveur
  // (qui peut lister des cosmetics d'un autre projet ou d'une version précédente du pack).
  useEffect(() => {
    let active = true;
    Promise.all([apiService.listPackCosmetics('nametag'), apiService.listPackCosmetics('ranktag')]).then(([n, r]) => {
      if (!active) return;
      setPackNametags(n.data?.items ?? []);
      setPackRanktags(r.data?.items ?? []);
    });
    return () => { active = false; };
  }, [activeGameId]);

  /** Référence : le premier container (ordre stable) qui porte la config premium. */
  const ref = loaded && all ? containers.find((c) => all[c.id]?.found) : undefined;
  const data = ref && all ? all[ref.id] : null;
  const refLook = useMemo(() => (data ? lookOf(data, grade) : null), [data, grade]);
  const key = draftKey('grade-apparence', activeGameId, grade);

  // Valeurs de la référence, ou le brouillon en attente (retour d'onglet ou de grade).
  useEffect(() => {
    if (!refLook) return;
    setOrig(refLook);
    const pending = getDraftPayload(key) as LookDraft | undefined;
    setRetry(!!pending?.retry);
    setNameTag(pending?.nameTag ?? refLook.nameTag);
    setRankTag(pending?.rankTag ?? refLook.rankTag);
  }, [refLook, key]);

  const dirty = !readOnly && !!ref && (retry || nameTag !== orig.nameTag || rankTag !== orig.rankTag);

  const scope: ScopeEntry[] = containers.map((c) => {
    const d = all?.[c.id];
    if (!d?.found || !refLook) return { containerId: c.id, containerName: c.name, status: 'SKIPPED_FILE_MISSING' };
    if (c.id === ref?.id) return { containerId: c.id, containerName: c.name, status: 'REFERENCE' };
    const differs = differingLook(lookOf(d, grade), refLook);
    return { containerId: c.id, containerName: c.name, status: differs.length ? 'DIFFERENT' : 'SAME', differs };
  });

  // Écart entre l'index déclaré au serveur et la position réelle dans le pack = mauvaise texture en jeu.
  // Vérifié sur chaque container qui porte la config, pas seulement sur la référence.
  const misaligned: string[] = [];
  const checkAlign = (where: string, label: string, value: string, pack: PackCosmetic[], declared: Record<string, number> | undefined) => {
    if (!value) return;
    const item = pack.find((p) => p.name === value);
    const current = declared?.[value];
    if (item && current !== undefined && current !== item.index) {
      misaligned.push(`${where} : ${label} « ${value} » serveur ${current} ≠ pack ${item.index}`);
    }
  };
  for (const c of containers) {
    const d = all?.[c.id];
    if (!d?.found) continue;
    checkAlign(c.name, 'ornement', nameTag, packNametags, d.nametagIndexes);
    checkAlign(c.name, 'rank tag', rankTag, packRanktags, d.ranktagIndexes);
  }

  /** Aligne un container sur le pack pour les cosmetics choisis : déclare ceux qu'il ne connaît pas ET
   * corrige un index périmé (pack réordonné), sinon le grade porte la texture d'un autre cosmetic.
   * Chaque container a son propre registre : on compare à CELUI du container écrit. */
  const ensureDeclared = async (cid: string, d: PremiumData) => {
    const todo: Array<{ type: 'nametag' | 'ranktag'; value: string; pack: PackCosmetic[]; declared: Record<string, number> }> = [
      { type: 'nametag', value: nameTag, pack: packNametags, declared: d.nametagIndexes ?? {} },
      { type: 'ranktag', value: rankTag, pack: packRanktags, declared: d.ranktagIndexes ?? {} },
    ];
    let changed = false;
    for (const t of todo) {
      if (!t.value) continue;
      const item = t.pack.find((p) => p.name === t.value);
      if (!item) continue; // absent du pack : rien à déclarer (valeur héritée, texture manquante)
      if (t.declared[t.value] === item.index) continue; // déjà aligné
      await apiService.declareContainerCosmetic(cid, t.type, t.value, item.index);
      changed = true;
    }
    return changed;
  };

  /** Écrit l'apparence du grade dans chaque container du projet qui porte la config premium. */
  const save = async () => {
    const values = { NameTagCosmetic: nameTag, RankTagCosmetic: rankTag };
    const entries: ContainerApplyEntry[] = [];
    for (const c of containers) {
      const d = all?.[c.id];
      const base = { containerId: c.id, containerName: c.name, path: d?.file ?? null };
      if (!d?.found) {
        entries.push({ ...base, status: 'SKIPPED_FILE_MISSING', message: 'Config premium introuvable (container non synchronisé ?).' });
        continue;
      }
      const declared = await ensureDeclared(c.id, d);
      if (!declared && differingLook(lookOf(d, grade), { nameTag, rankTag }).length === 0) {
        entries.push({ ...base, status: 'UNCHANGED', message: 'Déjà identique.' });
        continue;
      }
      const res = await apiService.saveContainerPremiumGroup(c.id, grade, values);
      entries.push(res.data?.success
        ? { ...base, status: 'WRITTEN', message: `Grade ${grade}` }
        : { ...base, status: 'FAILED', message: res.data?.message ?? res.error ?? 'Enregistrement en échec' });
    }
    const result = applyResultOf(entries);
    setReport(result);
    if (result.success) setOrig({ nameTag, rankTag });
    setRetry(!result.success);
    setReload((k) => k + 1);
    return result.success;
  };

  useSnapshotDraft({
    kind: 'grade-apparence',
    groupId: activeGameId,
    identifier: grade,
    label: `Apparence · ${grade} · tous les containers`,
    dirty,
    payload: { nameTag, rankTag, retry },
    save,
  });

  if (loaded && containers.length === 0) {
    // L'apparence est stockée dans la config d'un serveur : sans container déclaré, il n'y a nulle part où l'écrire.
    return (
      <p className={styles.muted}>
        Aucun container pour ce projet : l'apparence (ornement + rank tag) est écrite dans la config du serveur,
        donc il faut d'abord déclarer un container dans <strong>Paramètres généraux → Containers</strong> puis le synchroniser.
      </p>
    );
  }

  return (
    <div className={styles.essBlock}>
      <div className={styles.sectionLabel}><Sparkles size={14} /> Apparence : ornement &amp; rank tag</div>

      {!loaded || !all ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement…</span></div>
      ) : !ref || !data ? (
        <p className={styles.muted}>
          Config premium introuvable dans les containers du projet : synchronise-les dans Paramètres généraux → Containers.
        </p>
      ) : (
        <>
          <ContainerScope entries={scope} referencePath={data.file}
            labelOf={(k) => LOOK_LABELS[k as keyof Look] ?? k} />
          <div className={styles.essField}>
            <label className={styles.essLabel}>Ornement (NameTag)</label>
            <CosmeticSelect
              value={nameTag} onChange={setNameTag} disabled={readOnly}
              pack={packNametags} declared={data.nametags}
            />
          </div>

          <div className={styles.essField}>
            <label className={styles.essLabel}>Tag (RankTag)</label>
            <CosmeticSelect
              value={rankTag} onChange={setRankTag} disabled={readOnly}
              pack={packRanktags} declared={data.ranktags}
            />
          </div>

          <div className={styles.cosmeticRow}>
            <div className={styles.cosmeticCol}>
              <span className={styles.essLabel}>Aperçu ornement</span>
              <CosmeticPreview containerId={ref.id} type="nametag" name={nameTag}
                index={packNametags.find((p) => p.name === nameTag)?.index} />
            </div>
            <div className={styles.cosmeticCol}>
              <span className={styles.essLabel}>Aperçu RankTag</span>
              <CosmeticPreview containerId={ref.id} type="ranktag" name={rankTag}
                index={packRanktags.find((p) => p.name === rankTag)?.index} />
            </div>
          </div>

          {(staleOf(nameTag, packNametags) || staleOf(rankTag, packRanktags)) && (
            <span className={styles.error}>
              La valeur actuelle n'existe pas dans le resource pack de ce projet : rien ne s'affichera en jeu.
              Choisis un cosmetic de la liste « Dans le pack ».
            </span>
          )}

          {misaligned.length > 0 && (
            <span className={styles.error}>
              Index périmé côté serveur : {misaligned.join(' ; ')}. En jeu, c'est la texture du slot du serveur
              qui s'affiche, donc celle d'un autre cosmetic. Enregistrer ce grade corrige ces entrées dans chaque
              container ; utilise « Réaligner les index sur le pack » dans Configuration &gt; Cosmétiques des grades pour tout remettre d'aplomb.
            </span>
          )}

          {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

          <p className={styles.muted}>
            {retry
              ? "Un container n'a pas reçu le dernier enregistrement : enregistre à nouveau via la barre du bas."
              : dirty
                ? 'Modifications en attente : enregistre-les via la barre du bas, elles seront écrites dans tous les containers.'
                : 'Enregistré.'} Envoie ensuite les containers avec le bouton Envoyer.
          </p>
        </>
      )}
    </div>
  );
}
